import { Form, Col, Container, Row } from "react-bootstrap";
import styles from "./EditMyAccount.module.scss";
import React, { useEffect } from "react";
import { alertModal } from "../utils/modalController";
import Tabs from "./Tabs";
import AddressForm from "./AddressForm";
import FormLabel from "./FormLabel";
import CpfInput from "./CpfInput";
import Select from "react-select";
import PhoneNumberInput from "./PhoneNumberInput";
import SubmitButton from "./SubmitButton";
import { fetchAuth } from "../utils/fetchAuth";
import { formDataToJson } from "../utils/formDataToJson";

interface Props {
    user: User | null;
    permissions: Permission[];
}

const apiUrl = process.env.REACT_APP_API_URL || '';

const EditMyAccount: React.FC<Props> = ({ user, permissions }) => {
    const [sexos, setSexos] = React.useState<Sexo[]>([]);
    const [registros, setRegistros] = React.useState<Registro[]>([]);
    const [civilStatus, setCivilStatus] = React.useState<CivilStatus[]>([]);
    const [userData, setUserData] = React.useState<User | null>(null);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    useEffect(() => {
        const fetchAll = async () => {
            setIsLoading(true); // <-- Começa carregamento

            try {
                const [resSexos, resRegs, resCivilStatus] = await Promise.all([
                    fetch(apiUrl + '/contas/sexos'),
                    fetchAuth(apiUrl + '/contas/registros'),
                    fetch(apiUrl + '/contas/estados-civis'),
                ]);

                if(!resSexos.ok) {
                    const data = await resSexos.json();
                    throw new Error(data.message || 'Erro ao carregar sexos');
                }

                if(!resRegs.ok) {
                    const data = await resRegs.json();
                    throw new Error(data.message || 'Erro ao carregar registros');
                }

                if(!resCivilStatus.ok) {
                    const data = await resCivilStatus.json();
                    throw new Error(data.message || 'Erro ao carregar estados civis');
                }

                const [dataSexos, dataRegs, dataCivilStatus] = await Promise.all([
                    resSexos.json(), resRegs.json(), resCivilStatus.json()
                ]);

                setSexos(dataSexos);
                setRegistros(dataRegs);
                setCivilStatus(dataCivilStatus);

                if (user) {
                    const resUser = await fetchAuth(`${apiUrl}/my-account`);
                    const dataUser = await resUser.json();
                    if (!resUser.ok) {
                        throw new Error(dataUser.message || 'Erro desconhecido');
                    }

                    let dataProf = null;
                    if (dataUser?.professional) {
                        const resProf = await fetchAuth(`${apiUrl}/my-account/professional`);
                        dataProf = await resProf.json();
                        if (!resProf.ok) {
                            throw new Error(dataProf.message || 'Erro desconhecido');
                        }
                    }

                    let dataPatient = null;
                    if (dataUser?.patient) {
                        const resPatient = await fetchAuth(`${apiUrl}/my-account/patient`);
                        dataPatient = await resPatient.json();
                        if (!resPatient.ok) {
                            throw new Error(dataPatient.message || 'Erro desconhecido');
                        }
                    }

                    setUserData({
                        ...dataUser,
                        professional: dataProf,
                        patient: dataPatient
                    });
                } else {
                    setUserData(null);
                }
            } catch (err) {
                console.error('Erro ao carregar dados:', err);
                const message = err instanceof Error ? err.message : String(err);
                alertModal(message);
            } finally {
                setIsLoading(false); // <-- Termina carregamento
            }
        };

        fetchAll();
    }, [user]);

    return (
        <>
            <Container className={styles['image-container']}>
                <img
                    src='/assets/profile/no-photo.png'
                    alt="Foto do usuário"
                    className={styles["profile-photo"]}
                />
                <h3>
                    {userData?.name || 'Carregando...'}
                </h3>
            </Container>
            <Container className="my-4">
                <Tabs
                    guias={[
                        {
                            key: 'dados-pessoais',
                            titulo: 'Dados Pessoais',
                            conteudo: (
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        setSaving(true);
                                        fetchAuth(apiUrl + '/my-account/dados-pessoais', {
                                            method: 'PATCH',
                                            body: JSON.stringify(formDataToJson(new FormData(e.currentTarget))),
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                        })
                                            .then(async (res) => {
                                                const response_json = await res.json();
                                                if (!res.ok) {
                                                    return Promise.reject(new Error(response_json.message || 'Erro desconhecido'));
                                                }
                                                return response_json;
                                            })
                                            .then((data) => {
                                                setUserData(prev => prev ? { 
                                                    ...data, 
                                                    professional: prev.professional, 
                                                    patient: prev.patient 
                                                } : prev);
                                                alertModal('Dados salvos com sucesso!');
                                            })
                                            .catch(error => {
                                                console.error('Erro ao salvar dados:', error);
                                                alertModal(error);
                                            })
                                            .finally(() => {
                                                setSaving(false);
                                            });

                                    }}
                                >
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel required={true} htmlFor='nome'>Nome</FormLabel>
                                                    <Form.Control
                                                        name="nome"
                                                        id='nome'
                                                        className={styles['input-control']}
                                                        value={isLoading ? 'Carregando...' : userData?.name}
                                                        onChange={(e) => setUserData({ ...userData!, name: e.target.value })}
                                                        required
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel required={true} htmlFor='cpf'>CPF:</FormLabel>
                                                    <CpfInput
                                                        required={true}
                                                        id="cpf"
                                                        className={`form-control ${styles['input-control']}`}
                                                        defaultValue={isLoading ? 'Carregando...' : userData?.cpf}
                                                        onChange={(value) => setUserData({ ...userData!, cpf: value })}
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='birthday'>Data de Nascimento:</FormLabel>
                                                    <Form.Control
                                                        name="birthday"
                                                        id='birthday'
                                                        className={styles['input-control']}
                                                        type='date'
                                                        value={isLoading ? '' : userData?.birthday}
                                                        disabled={isLoading}
                                                        onChange={(e) => {
                                                            setUserData({ ...userData!, birthday: e.target.value });
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='civil_status'>Estado Civil:</FormLabel>
                                                    <Select
                                                        name="civil_status"
                                                        id="civil_status"
                                                        classNames={{
                                                            control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                                                            menu: () => styles['select-menu'],
                                                            option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                                                            container: () => styles['select-container'],
                                                        }}
                                                        options={civilStatus.map(s => ({
                                                            label: s.name,
                                                            value: s.code, // aqui é string, como "M" ou "F"
                                                        }))}
                                                        value={userData?.civil_status ? { label: civilStatus.find(s => s.code === userData.civil_status)?.name || '', value: userData.civil_status } : null}
                                                        onChange={(selected) => setUserData({ ...userData!, civil_status: selected?.value ?? undefined })}
                                                        isDisabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='sexo-biologico'>Sexo biológico:</FormLabel>
                                                    <Select
                                                        name="sexo-biologico"
                                                        id="sexo-biologico"
                                                        classNames={{
                                                            control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                                                            menu: () => styles['select-menu'],
                                                            option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                                                            container: () => styles['select-container'],
                                                        }}
                                                        options={sexos.map(s => ({
                                                            label: s.name,
                                                            value: s.code, // aqui é string, como "M" ou "F"
                                                        }))}
                                                        value={userData?.sexo ? { label: sexos.find(s => s.code === userData?.sexo)?.name || '', value: userData?.sexo } : null}
                                                        onChange={(selected) => setUserData({ ...userData!, sexo: selected?.value ?? undefined })}
                                                        isDisabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel required={true} htmlFor='email'>E-mail:</FormLabel>
                                                    <Form.Control
                                                        name="email"
                                                        type='email'
                                                        id='email'
                                                        className={styles['input-control']}
                                                        value={isLoading ? 'Carregando...' : userData?.email}
                                                        onChange={(e) => setUserData({ ...userData!, email: e.target.value })}
                                                        disabled={isLoading}
                                                        required />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='email-alternativo'>E-mail alternativo:</FormLabel>
                                                    <Form.Control
                                                        name="email-alternativo"
                                                        id='email-alternativo'
                                                        className={styles['input-control']}
                                                        type="email"
                                                        value={isLoading ? 'Carregando...' : userData?.email_alternativo}
                                                        onChange={(e) => setUserData({ ...userData!, email_alternativo: e.target.value })}
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='password'>Senha:</FormLabel>
                                                    <Form.Control
                                                        name="password"
                                                        id='password'
                                                        className={styles['input-control']}
                                                        type='password'
                                                        placeholder={userData?.have_password ? 'Deixe em branco para não alterar' : undefined}
                                                        value={undefined} // Placeholder para o valor, se necessário
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='password-confirm'>Confirme a senha:</FormLabel>
                                                    <Form.Control
                                                        name="password-confirm"
                                                        id='password-confirm'
                                                        className={styles['input-control']}
                                                        type="password"
                                                        placeholder={userData?.have_password ? 'Deixe em branco para não alterar' : undefined}
                                                        value={undefined} // Placeholder para o valor, se necessário
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md>
                                                <Form.Group>
                                                    <FormLabel id='personal-telefone'>Telefone:</FormLabel>
                                                    <PhoneNumberInput
                                                        id="personal-telefone"
                                                        className={styles['input-control']}
                                                        defaultValue={userData?.telefone ? `+${userData.telefone.ddi}${userData.telefone.numero}` : undefined}
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>

                                    <AddressForm
                                        id={"main"}
                                        defaultCep={userData?.address?.cep}
                                        defaultComplemento={userData?.address?.complemento}
                                        defaultNumero={userData?.address?.numero}
                                        disabled={isLoading}
                                    />
                                    <Row>
                                        <Col>
                                            <SubmitButton
                                                className={styles['buttons']}
                                                disabled={isLoading}
                                            >
                                                {saving ? 'Salvando...' : 'Salvar Dados'}
                                            </SubmitButton>
                                        </Col>
                                    </Row>
                                </Form>
                            )
                        },
                        ...(permissions.some(perm => perm === permissions.find(
                            p => p.name === 'Profissional'
                        )) ? [{
                            key: 'dados-profissionais',
                            titulo: 'Dados Profissionais',
                            conteudo: (
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        setSaving(true);
                                        fetchAuth(apiUrl + '/my-account/dados-profissionais', {
                                            method: 'PATCH',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify(formDataToJson(new FormData(e.currentTarget))),
                                        })
                                            .then(async (res) => {
                                                const response_json = await res.json();
                                                if (!res.ok) {
                                                    return Promise.reject(new Error(response_json.message || 'Erro desconhecido'));
                                                }
                                                return response_json;
                                            })
                                            .then(response_json => {
                                                setUserData(prev => prev ? { 
                                                    ...prev, 
                                                    professional: response_json,
                                                    patient: prev.patient
                                                } : prev);
                                                alertModal('Dados profissionais salvos com sucesso!');
                                            })
                                            .catch(error => {
                                                console.error('Erro ao salvar dados profissionais:', error);
                                                alertModal(error);
                                            })
                                            .finally(() => {
                                                setSaving(false);
                                            });
                                    }
                                    }
                                >
                                    <Form.Group className="mb-3">
                                        <FormLabel htmlFor='professional-name'>Nome:</FormLabel>
                                        <Form.Control
                                            name="professional-name"
                                            id='professional-name'
                                            className={styles['input-control']}
                                            value={isLoading ? 'Carregando...' : userData?.professional?.name}
                                            onChange={(e) => setUserData({
                                                ...userData!,
                                                professional: {
                                                    ...userData?.professional,
                                                    id: userData?.professional?.id ?? -1,
                                                    name: e.target.value
                                                }
                                            })}
                                            disabled={isLoading}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='registro'>Registro:</FormLabel>
                                                    <Select
                                                        name="registro"
                                                        classNames={{
                                                            control: (state) => `${styles['select-control']} ${state.isFocused ? styles['focused'] : ''}`,
                                                            menu: () => styles['select-menu'],
                                                            option: (state) => `${styles['select-option']} ${state.isSelected ? styles['selected'] : ''}`,
                                                            container: () => styles['select-container'],
                                                        }}
                                                        id="registro"
                                                        options={registros.map(r => ({ label: r.name, value: r.code }))}
                                                        onChange={(value) => setUserData({
                                                            ...userData!, professional: {
                                                                ...userData?.professional,
                                                                id: userData?.professional?.id ?? -1,
                                                                registro: value?.value || undefined
                                                            }
                                                        })}
                                                        value={
                                                            userData?.professional?.registro
                                                                ? { label: registros.find(r => r.code === userData.professional?.registro)?.name || '', value: userData.professional?.registro }
                                                                : null
                                                        }
                                                        isDisabled={isLoading}
                                                    />

                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <FormLabel htmlFor='registro-numero'>Número:</FormLabel>
                                                    <Form.Control
                                                        name="registro-numero"
                                                        id='registro-numero'
                                                        className={styles['input-control']}
                                                        value={isLoading ? 'Carregando...' : userData?.professional?.registro_numero}
                                                        onChange={(e) => setUserData({
                                                            ...userData!, professional: {
                                                                ...userData?.professional,
                                                                id: userData?.professional?.id ?? -1,
                                                                registro_numero: e.target.value
                                                            }
                                                        })}
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Row>
                                            <Col md>
                                                <FormLabel htmlFor='professional-telefone'>Telefone:</FormLabel>
                                                <PhoneNumberInput
                                                    id="professional-telefone"
                                                    className={styles['input-control']}
                                                    defaultValue={userData?.professional?.telefone ? `+${userData.professional.telefone.ddi}${userData.professional.telefone.numero}` : undefined}
                                                    disabled={isLoading}
                                                />
                                            </Col>
                                        </Row>
                                    </Form.Group>

                                    <AddressForm
                                        id={"professional"}
                                        defaultCep={userData?.professional?.address?.cep}
                                        defaultComplemento={userData?.professional?.address?.complemento}
                                        defaultNumero={userData?.professional?.address?.numero}
                                        disabled={isLoading}
                                    />
                                    <Row>
                                        <Col>
                                            <SubmitButton
                                                className={styles['buttons']}
                                                disabled={isLoading}
                                            >
                                                {saving ? 'Salvando...' : 'Salvar Dados Profissionais'}
                                            </SubmitButton>
                                        </Col>
                                    </Row>
                                </Form>
                            )
                        }] : []),
                        ...(permissions.some(perm => perm === permissions.find(
                            p => p.name === 'Paciente'
                        )) ? [{
                            key: 'dados-adicionais',
                            titulo: 'Dados Adicionais',
                            conteudo: (
                                <Form
                                    onSubmit={
                                        (e) => {
                                            e.preventDefault();
                                            setSaving(true);
                                            fetchAuth(apiUrl + '/my-account/dados-adicionais', {
                                                method: 'PATCH',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                },
                                                body: JSON.stringify(formDataToJson(new FormData(e.currentTarget))),
                                            })
                                                .then(async (res) => {
                                                    const response_json = await res.json();
                                                    if (!res.ok) {
                                                        return Promise.reject(new Error(response_json.message || 'Erro desconhecido'));
                                                    }
                                                    return response_json;
                                                })
                                                .then(response_json => {
                                                    setUserData(prev => prev ? { 
                                                        ...prev, 
                                                        patient: response_json,
                                                        professional: prev.professional
                                                    } : prev);
                                                    alertModal('Dados adicionais salvos com sucesso!');
                                                })
                                                .catch(error => {
                                                    console.error('Erro ao salvar dados adicionais:', error);
                                                    alertModal(error);
                                                })
                                                .finally(() => {
                                                    setSaving(false);
                                                });
                                        }
                                    }
                                >
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <FormLabel htmlFor='profissao' >Profissão:</FormLabel>
                                                <Form.Control
                                                    name="profissao"
                                                    id='profissao'
                                                    className={styles['input-control']}
                                                    defaultValue={isLoading ? 'Carregando...' : userData?.patient?.profissao}
                                                    disabled={isLoading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <SubmitButton
                                                className={styles['buttons']}
                                                disabled={isLoading}
                                            >
                                                Salvar Dados Adicionais
                                            </SubmitButton>
                                        </Col>
                                    </Row>
                                </Form>
                            )
                        }] : []),
                    ]}
                />
            </Container>
        </>
    );
};

export default EditMyAccount;
